<script>
    import { debounce } from "@/utils";
    import BaseInput from "@main/packages/BaseInput";
    import FormSwitch from "@madrobots/components/ui/FormSwitch";

    export default {
        name: "GiftDate",
        components: {
            BaseInput,
            FormSwitch,
        },
        props: {
            errors: Object,
        },
        data() {
            return {
                deliveryType: "email", //email, sms
                form: {
                    senderEmail: "",
                    recipientEmail: "",
                    recipientPhone: "",
                    date: "",
                    reportDelivery: false,
                },
            };
        },
        watch: {
            form: {
                handler: debounce(function (value) {
                    if (value.date.length === 0) {
                        this.form.date = this.getDate();
                    }
                    this.$emit("on-update", value);
                }, 500),
                deep: true,
            },
        },
        computed: {
            types() {
                return [
                    { text: "На E-mail", state: "email" },
                    { text: "По SMS", state: "sms" },
                ];
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.form.date = this.getDate();
            });
        },
        methods: {
            getDate() {
                const date = new Date();
                const month =
                    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
                const day = date.getDate() >= 10 ? date.getDate() : `0${date.getDate()}`;
                return `${date.getFullYear()}-${month}-${day}`;
            },
            onSelectType(type) {
                this.deliveryType = type;
                this.form.recipientEmail = "";
                this.form.recipientPhone = "";
                this.$emit("on-change-type", type);
            },
            resetError(name) {
                this.$emit("on-reset-error", { type: "formDate", name });
            },
        },
    };
</script>

<template>
    <section id="gift-date" class="gift-date gift-section">
        <div class="gift-date__main">
            <h2 class="gift-section__title">
                Выберите дату и&nbsp;способ доставки сертификата
            </h2>
            <div class="gift-section__text">
                Мы&nbsp;отправим сертификат получателю в&nbsp;выбранную дату
            </div>

            <div class="gift-date__group wrap">
                <div class="gift-date__input">
                    <input
                        class="base-input__field base-input--secondary"
                        type="date"
                        :min="getDate()"
                        v-model="form.date"
                    />
                </div>

                <div v-if="form.date === getDate()" class="gift-date__notation">
                    Будет отправлен сейчас
                </div>

                <div class="gift-date__checkbox">
                    <form-switch v-model="form.reportDelivery"></form-switch>
                    <div class="gift-date__notation">
                        Сообщить мне, когда сертификат будет доставлен
                    </div>
                </div>
            </div>

            <div class="gift-date__types">
                <button
                    v-for="type in types"
                    :key="type.state"
                    class="gift-date__type"
                    :class="{ 'is-active': type.state === deliveryType }"
                    @click="onSelectType(type.state)"
                >
                    {{ type.text }}
                </button>
            </div>

            <form class="gift-date__form">
                <div class="gift-date__group">
                    <div class="gift-date__input">
                        <base-input
                            v-model="form.senderEmail"
                            name="senderEmail"
                            type="email"
                            variant="secondary"
                            placeholder="Email отправителя"
                            :error-text="errors.senderEmail"
                            @error="resetError"
                        ></base-input>
                    </div>
                    <div class="gift-date__notation">
                        Мы отправим электронный чек на этот адрес
                    </div>
                </div>

                <div v-if="deliveryType === 'email'" class="gift-date__group">
                    <div class="gift-date__input">
                        <base-input
                            v-model="form.recipientEmail"
                            name="recipientEmail"
                            type="email"
                            variant="secondary"
                            key="email"
                            placeholder="Email получателя карты"
                            :error-text="errors.recipientEmail"
                            @error="resetError"
                        ></base-input>
                    </div>
                    <div class="gift-date__notation">
                        На этот email придет поздравление и ссылка на подарок!
                    </div>
                </div>

                <div v-if="deliveryType === 'sms'" class="gift-date__group">
                    <div class="gift-date__input">
                        <base-input
                            v-model="form.recipientPhone"
                            type="tel"
                            variant="secondary"
                            name="phone"
                            key="phone"
                            placeholder="Телефон получателя карты"
                            :error-text="errors.recipientPhone"
                            @error="resetError"
                        ></base-input>
                    </div>

                    <div class="gift-date__notation">
                        На этот телефон придет поздравление и ссылка на подарок!
                    </div>
                </div>
            </form>
        </div>

        <div class="gift-date__image">
            <img src="/static/madrobots/gift/date/image.png" alt="" />
        </div>
    </section>
</template>

<style lang="scss">
    .gift-date {
        @include bp($bp-desktop-sm) {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
    }

    .gift-date__main {
        margin-bottom: 30px;
        @include bp($bp-desktop-sm) {
            flex-shrink: 0;
            flex-basis: 500px;
            max-width: 500px;
            margin-bottom: 0;
        }
        @include bp($bp-desktop-lg) {
            flex-basis: 580px;
            max-width: 580px;
        }
    }

    .gift-date__image {
        position: relative;
        &::before {
            content: "";
            display: block;
            padding-bottom: 117.64%;
        }
        img {
            @include center-absolute(0);
        }
        @include bp($bp-desktop-sm) {
            flex-shrink: 0;
            flex-basis: 400px;
            max-width: 400px;
        }
        @include bp($bp-desktop-lg) {
            flex-basis: 480px;
            max-width: 480px;
        }
    }

    .gift-date__types {
        display: flex;
        align-items: center;
        margin: 20px 0 10px;
        @include bp($bp-desktop-sm) {
            max-width: 280px;
        }
        @include bp($bp-desktop-lg) {
            max-width: 360px;
        }
    }

    .gift-date__type {
        flex-basis: 50%;
        max-width: 50%;
        padding: 12px 18px;
        color: var(--primary-text);
        font-size: 16px;
        font-weight: 700;
        line-height: 22px;
        background-color: #fff;
        transition: color 0.2s ease-in, background-color 0.2s ease-in;
        &:hover {
            color: var(--primary);
        }
        &:first-child {
            border-radius: 10px 0 0 10px;
        }
        &:last-child {
            border-radius: 0 10px 10px 0;
        }
        &.is-active {
            color: #fff;
            background-color: var(--primary);
            pointer-events: none;
        }
    }

    .gift-date__group {
        &:not(:last-child) {
            margin-bottom: 10px;
        }
        @include bp($bp-desktop-sm) {
            display: flex;
            align-items: center;
            &.wrap {
                flex-wrap: wrap;
            }
        }
    }

    .gift-date__input {
        width: 100%;
        margin-bottom: 5px;
        @include bp($bp-desktop-sm) {
            max-width: 280px;
            margin-bottom: 0;
        }
        @include bp($bp-desktop-lg) {
            max-width: 360px;
        }
    }

    .gift-date__notation {
        margin-left: 5px;
        color: var(--secondary-text);
        font-size: 12px;
        line-height: 16px;
        @include bp($bp-desktop-sm) {
            margin-left: 10px;
        }
    }

    .gift-date__checkbox {
        display: flex;
        align-items: center;
        width: 100%;
        margin-top: 5px;
        @include bp($bp-desktop-sm) {
            margin-top: 10px;
        }
    }
</style>
